/**
 * The filter rail on My Events.
 *
 * One generic group renderer rather than a block per filter: the four groups
 * differ only in their options, and writing them out separately is how four
 * filter lists end up with four slightly different focus rings.
 *
 * Every option carries a live count taken from the member's full registration
 * set, so a group shows what choosing it would actually yield — including zero.
 * An option that would return nothing is disabled rather than hidden, because a
 * filter that silently drops its own options makes the rail look broken.
 */
import { SlidersHorizontal, X } from "lucide-react";

export type FilterOption = {
  value: string;
  label: string;
  count: number;
};

export type FilterGroup = {
  key: string;
  label: string;
  options: FilterOption[];
  value: string;
  onChange: (value: string) => void;
};

export default function MyEventsFilters({
  groups,
  activeCount,
  onClear,
  resultCount,
}: {
  groups: FilterGroup[];
  /** How many groups are away from their default, for the Clear affordance. */
  activeCount: number;
  onClear: () => void;
  resultCount: number;
}) {
  return (
    <aside className="my-events-filters" aria-label="Filter your events">
      <header className="my-events-filters__head">
        <h2>
          <SlidersHorizontal size={15} aria-hidden="true" />
          Filter
        </h2>
        {activeCount > 0 && (
          <button type="button" onClick={onClear} className="my-events-filters__clear">
            Clear <X size={13} aria-hidden="true" />
          </button>
        )}
      </header>

      <p className="my-events-filters__result" aria-live="polite">
        {resultCount === 0
          ? "No events match"
          : `${resultCount} event${resultCount === 1 ? "" : "s"}`}
      </p>

      {groups.map(group => (
        <fieldset className="my-events-filters__group" key={group.key}>
          <legend>{group.label}</legend>
          {group.options.map(option => {
            const selected = group.value === option.value;
            // Never disable what is currently chosen, or the member cannot see
            // why the list is empty or get back out of it.
            const unavailable = option.count === 0 && !selected;
            return (
              <button
                type="button"
                key={option.value}
                aria-pressed={selected}
                disabled={unavailable}
                onClick={() => group.onChange(option.value)}
                className={selected ? "is-selected" : undefined}
              >
                <span>{option.label}</span>
                <em>{option.count}</em>
              </button>
            );
          })}
        </fieldset>
      ))}

      <style>{`
        .my-events-filters{position:sticky;top:96px;display:grid;gap:1.15rem;align-content:start;padding:1.15rem 1.2rem;border:1px solid rgba(255,255,255,.09);border-radius:1rem;background:rgba(10,16,30,.84);box-shadow:inset 0 1px 0 rgba(255,255,255,.04)}
        .my-events-filters__head{display:flex;align-items:center;justify-content:space-between;gap:.75rem}
        .my-events-filters__head h2{display:flex;align-items:center;gap:.45rem;margin:0;color:#fff;font-family:'Space Grotesk',sans-serif;font-size:.82rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase}
        .my-events-filters__head h2 svg{color:#F9A8D4}
        .my-events-filters__clear{display:inline-flex;align-items:center;gap:.3rem;padding:.2rem .45rem;border:1px solid rgba(255,255,255,.14);border-radius:.4rem;color:#CBD5E1;background:transparent;font-family:'Inter',sans-serif;font-size:.7rem;font-weight:650;cursor:pointer}
        .my-events-filters__clear:hover,.my-events-filters__clear:focus-visible{outline:0;border-color:rgba(244,114,182,.5);color:#fff}
        .my-events-filters__result{margin:0;color:#94A3B8;font-family:'Inter',sans-serif;font-size:var(--chi-body-dense);line-height:1.5}
        .my-events-filters__group{display:grid;gap:.3rem;margin:0;padding:0;border:0}
        .my-events-filters__group legend{padding:0 0 .45rem;color:#64748B;font-family:'Space Grotesk',sans-serif;font-size:.66rem;font-weight:800;letter-spacing:.09em;text-transform:uppercase}
        .my-events-filters__group button{display:flex;align-items:center;justify-content:space-between;gap:.6rem;width:100%;min-height:2.15rem;padding:.4rem .6rem;border:1px solid transparent;border-radius:.5rem;color:#CBD5E1;background:transparent;font-family:'Inter',sans-serif;font-size:.8rem;font-weight:600;text-align:left;cursor:pointer;transition:background .15s,color .15s,border-color .15s}
        .my-events-filters__group button em{color:#64748B;font-family:'Space Grotesk',sans-serif;font-size:.72rem;font-style:normal;font-weight:750}
        .my-events-filters__group button:hover:not(:disabled),.my-events-filters__group button:focus-visible:not(:disabled){outline:0;color:#fff;background:rgba(255,255,255,.05)}
        .my-events-filters__group button.is-selected{border-color:rgba(233,30,140,.42);color:#fff;background:rgba(233,30,140,.14)}
        .my-events-filters__group button.is-selected em{color:#F9A8D4}
        .my-events-filters__group button:disabled{opacity:.38;cursor:not-allowed}
        @media(max-width:1000px){.my-events-filters{position:static;top:auto}}
      `}</style>
    </aside>
  );
}
